/**
 * <s-slider> — native scroll-snap slider controller.
 * The track (`[data-slider-track]`) scrolls natively; this element only adds
 * button navigation and progress feedback.
 *
 * Commands (buttons with commandfor="<slider id>"):
 *   --prev / --next — scroll by one slide.
 *   --go            — scroll to the slide at data-index.
 *
 * Feedback hooks inside the element (or referenced by `for`-less ids):
 *   [data-slider-progress] — width follows the scrolled ratio.
 *   [data-slider-bar]      — one per slide or page; the current one gets data-active.
 *   [data-slider-counter]  — receives "current / total".
 *   [data-slider-thumb]    — gallery thumbnails; the current one gets data-active.
 *
 * Mouse drag: a track marked [data-slider-drag] can be dragged with the mouse
 * (touch already scrolls natively); it carries data-dragging while held and
 * settles on the nearest slide on release.
 *
 * Autoplay: data-autoplay="<ms>" on the element advances one slide per delay
 * and loops back to the first. It only runs while the track actually scrolls
 * (e.g. mobile only), the slider is on screen and nobody is touching, hovering
 * or focusing it, and never with reduced motion.
 */
class SSlider extends HTMLElement {
  #ac;
  #track;
  #frame = 0;
  #drag = null;
  #suppressClick = false;
  #autoplayTimer = 0;
  #autoplayPaused = false;
  #autoplayVisible = false;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.#step(-1);
      if (event.command === '--next') this.#step(1);
      if (event.command === '--go') this.goTo(Number.parseInt(event.source?.dataset.index || '0', 10));
    }, opts);

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    }, { ...opts, passive: true });

    window.addEventListener('resize', () => this.#update(), opts);
    if (this.#track.hasAttribute('data-slider-drag')) this.#bindDrag(opts);
    this.#update();
    this.#bindAutoplay(opts);
  }

  #bindDrag(opts) {
    const track = this.#track;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.#drag = { x: event.clientX, left: track.scrollLeft, moved: false, id: event.pointerId };
    }, opts);

    track.addEventListener('pointermove', (event) => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 5) return;
        drag.moved = true;
        // Snap fights a programmatic scroll on every frame; it comes back on release.
        track.style.scrollSnapType = 'none';
        track.setAttribute('data-dragging', '');
        track.setPointerCapture(drag.id);
      }
      track.scrollLeft = drag.left - dx;
    }, opts);

    const release = (event) => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.#drag = null;
      if (!drag.moved) return;
      track.removeAttribute('data-dragging');
      if (track.hasPointerCapture(drag.id)) track.releasePointerCapture(drag.id);
      const index = Math.min(this.#currentIndex(), Math.max(this.#slides.length - 1, 0));
      track.style.scrollSnapType = '';
      this.goTo(index);
      // The click that ends a drag must not follow a link; it fires right after pointerup.
      this.#suppressClick = true;
      setTimeout(() => { this.#suppressClick = false; }, 0);
    };
    track.addEventListener('click', (event) => {
      if (!this.#suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, { ...opts, capture: true });
    track.addEventListener('pointerup', release, opts);
    track.addEventListener('pointercancel', release, opts);
    // Native image/link dragging would hijack the gesture.
    track.addEventListener('dragstart', (event) => event.preventDefault(), opts);
  }

  #bindAutoplay(opts) {
    const delay = Number.parseInt(this.dataset.autoplay || '', 10);
    if (!delay || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const pause = () => { this.#autoplayPaused = true; this.#scheduleAutoplay(delay); };
    const resume = () => { this.#autoplayPaused = false; this.#scheduleAutoplay(delay); };
    this.addEventListener('pointerenter', pause, opts);
    this.addEventListener('pointerleave', resume, opts);
    this.addEventListener('focusin', pause, opts);
    this.addEventListener('focusout', resume, opts);
    this.#track.addEventListener('touchstart', pause, { ...opts, passive: true });
    this.#track.addEventListener('touchend', resume, { ...opts, passive: true });
    const observer = new IntersectionObserver(([entry]) => {
      this.#autoplayVisible = entry.isIntersecting;
      this.#scheduleAutoplay(delay);
    });
    observer.observe(this);
    opts.signal.addEventListener('abort', () => { observer.disconnect(); clearTimeout(this.#autoplayTimer); });
  }

  #scheduleAutoplay(delay) {
    clearTimeout(this.#autoplayTimer);
    if (this.#autoplayPaused || !this.#autoplayVisible) return;
    this.#autoplayTimer = setTimeout(() => {
      const track = this.#track;
      const max = track.scrollWidth - track.clientWidth;
      if (max > 1) {
        if (Math.abs(track.scrollLeft) >= max - 1) this.goTo(0);
        else this.#step(1);
      }
      this.#scheduleAutoplay(delay);
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return [...this.#track.children].filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
  }

  get #behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #slideSize() {
    const [first, second] = this.#slides;
    if (!first) return this.#track.clientWidth;
    if (second) return Math.abs(second.offsetLeft - first.offsetLeft);
    return first.getBoundingClientRect().width;
  }

  #step(direction) {
    const rtl = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollBy({ left: direction * rtl * this.#slideSize(), behavior: this.#behavior });
  }

  goTo(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#slides[0].offsetLeft, behavior: this.#behavior });
  }

  #currentIndex() {
    const size = this.#slideSize() || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / size);
  }

  #update() {
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const ratio = max > 0 ? Math.abs(track.scrollLeft) / max : 1;
    const visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
    const total = this.#slides.length;
    const index = Math.min(this.#currentIndex(), Math.max(total - 1, 0));

    this.querySelectorAll('[data-slider-progress]').forEach((bar) => {
      bar.style.width = `${Math.min(100, (visible + (1 - visible) * ratio) * 100)}%`;
    });

    const bars = this.querySelectorAll('[data-slider-bar]');
    const barIndex = bars.length >= total ? index : Math.round(ratio * (bars.length - 1));
    bars.forEach((bar, i) => bar.toggleAttribute('data-active', i === barIndex));

    this.querySelectorAll('[data-slider-thumb]').forEach((thumb, i) => {
      thumb.toggleAttribute('data-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.querySelectorAll('[data-slider-counter]').forEach((counter) => {
      counter.textContent = `${index + 1} / ${total}`;
    });

    this.querySelectorAll('[data-slider-prev]').forEach((btn) => btn.toggleAttribute('data-disabled', track.scrollLeft <= 1));
    this.querySelectorAll('[data-slider-next]').forEach((btn) => btn.toggleAttribute('data-disabled', Math.abs(track.scrollLeft) >= max - 1));
  }
}

if (!customElements.get('s-slider')) {
  customElements.define('s-slider', SSlider);
}
